import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import InterviewExperience from '../interview/InterviewExperience';
import { useSettings } from '../../contexts/SettingsContext';
import { claimAiLaunch, getAiLaunchStatus, getStatus, releaseAiLaunch, renewAiLaunch, setInterviewLease } from './utils/aiInterviewApi';

const pad = (n) => String(n).padStart(2, '0');
const countdownParts = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    h: pad(Math.floor(total / 3600)),
    m: pad(Math.floor((total % 3600) / 60)),
    s: pad(total % 60),
  };
};

const _WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const _MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Friendly schedule text: "Tue, Jun 30, 2026 · 5:00 PM IST".
// IMPORTANT: scheduled_date ("YYYY-MM-DD") + scheduled_time ("HH:MM") are stored as the interview's
// WALL-CLOCK values in the company's timezone. We format them DETERMINISTICALLY (pure arithmetic, never
// `new Date("...T13:05")`) so every device shows the SAME string — passing a wall-clock through Date()
// silently reinterprets it in the viewer's own timezone and drifts by the UTC offset (the "+5:30" bug).
// The tz label (e.g. "IST") makes the zone explicit so a candidate in another region isn't misled.
const prettySchedule = (dateStr, timeStr, tzLabel = '') => {
  if (!dateStr) return '';
  let datePart = dateStr;
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (dm) {
    const [, y, mo, day] = dm;
    // UTC anchor purely to derive the weekday — no offset math touches the displayed values.
    const anchor = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(day)));
    if (!Number.isNaN(anchor.getTime())) {
      datePart = `${_WD[anchor.getUTCDay()]}, ${_MO[Number(mo) - 1]} ${Number(day)}, ${y}`;
    }
  }
  let timePart = '';
  const tm = /^(\d{1,2}):(\d{2})/.exec(String(timeStr || ''));
  if (tm) {
    let h = Number(tm[1]);
    const min = tm[2];
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    timePart = `${h}:${min} ${ampm}`;
  }
  // Show the clean zone abbreviation only: "UTC+5:30 (IST)" → "IST"; "IST" → "IST".
  // (The raw offset like "+5:30" is just the zone's UTC offset — it isn't added to the time — but it
  // reads as clutter, so prefer the parenthetical abbreviation when the setting carries one.)
  const abbr = tzLabel ? (((/\(([^)]+)\)/.exec(tzLabel) || [])[1]) || tzLabel).trim() : '';
  const lbl = abbr ? ` ${abbr}` : '';
  return timePart ? `${datePart} · ${timePart}${lbl}` : datePart;
};

// ── presentational atoms ───────────────────────────────────────────────────
const BrandMark = ({ tone = 'brand' }) => {
  const tones = {
    brand: 'from-indigo-500 to-violet-600 shadow-indigo-500/30',
    success: 'from-emerald-500 to-teal-600 shadow-emerald-500/30',
    error: 'from-rose-500 to-red-600 shadow-rose-500/30',
    muted: 'from-slate-400 to-slate-500 shadow-slate-500/30',
  };
  return (
    <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${tones[tone]} text-white shadow-lg`}>
      {tone === 'success' ? (
        <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
        </svg>
      ) : tone === 'error' ? (
        <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 9v4m0 4h.01M10.3 3.86l-8.07 14A1.5 1.5 0 003.53 20h16.94a1.5 1.5 0 001.3-2.14l-8.07-14a1.5 1.5 0 00-2.6 0z" />
        </svg>
      ) : (
        <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l.7 1.8L21.5 16.5l-1.8.7L19 19l-.7-1.8L16.5 16.5l1.8-.7L19 14z" />
        </svg>
      )}
    </div>
  );
};

const Shell = ({ children }) => {
  // Proper, centered brand lockup on EVERY state (mobile + desktop) — the old design only had a tiny
  // fixed corner tag, which read as "no branding" on phones. White-label aware (uses tenant app name/logo).
  const { settings } = useSettings();
  const name = settings?.app?.name || 'Wizbox';
  const logo = settings?.app?.logo;
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-10 dark:from-[#0a0a12] dark:to-[#11111c]">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl shadow-slate-300/30 dark:border-white/10 dark:bg-[#15151f] dark:shadow-black/40">
        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
        <div className="flex items-center justify-center gap-2 px-7 pt-6">
          {logo ? (
            <img src={logo} alt={name} className="h-7 w-7 rounded-lg object-contain" />
          ) : (
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-bold uppercase text-white">{name.slice(0, 1)}</span>
          )}
          <span className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-lg">
            {name} <span className="font-normal text-slate-400">Interview Suite</span>
          </span>
        </div>
        <div className="px-7 pb-9 pt-6 text-center">{children}</div>
      </div>
    </div>
  );
};

const TimeTile = ({ value, label, highlight }) => (
  <div className="flex flex-col items-center">
    <div className={`flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-2xl text-[2rem] font-bold tabular-nums ring-1 ring-inset transition-colors
      ${highlight
        ? 'bg-indigo-50 text-indigo-600 ring-indigo-200 dark:bg-violet-500/15 dark:text-violet-300 dark:ring-violet-500/30'
        : 'bg-slate-900/[0.04] text-slate-800 ring-slate-900/5 dark:bg-white/[0.06] dark:text-white dark:ring-white/10'}`}>
      {value}
    </div>
    <span className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</span>
  </div>
);

const Colon = () => (
  <span className="-mt-6 text-3xl font-bold text-slate-300 dark:text-slate-600">:</span>
);

const AIInterviewLaunchPage = () => {
  const { token } = useParams();
  const ownerStorageKey = `ai-interview:owner:${token}`;
  const { settings } = useSettings();
  const appName = settings?.app?.name || 'Wizbox';
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [serverClockOffset, setServerClockOffset] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [maxRemaining, setMaxRemaining] = useState(0);
  const [leaseReady, setLeaseReady] = useState(false);
  const [leaseBlocked, setLeaseBlocked] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [leaseRetryAt, setLeaseRetryAt] = useState(0);
  const ownerRef = useRef('');
  const unlockLocalRef = useRef(null);
  const claimedRef = useRef(false);
  const claimInFlightRef = useRef(false);
  const acquireLeaseRef = useRef(null);
  const lifecycleRef = useRef(0);
  const hiddenRef = useRef(false);

  const loseLease = useCallback(() => {
    claimedRef.current = false;
    setInterviewLease('', null);
    unlockLocalRef.current?.();
    unlockLocalRef.current = null;
    setLeaseReady(false);
    setLeaseBlocked(true);
    setAccepted(false);
  }, []);

  const acquireLocal = async () => {
    if (!navigator.locks?.request) {
      if (typeof BroadcastChannel === 'undefined') return true;
      const channel = new BroadcastChannel(`ai-interview:${token}`);
      let otherActive = false;
      channel.onmessage = ({ data }) => {
        if (data === 'probe' && claimedRef.current) channel.postMessage('active');
        if (data === 'active') otherActive = true;
      };
      channel.postMessage('probe');
      await new Promise((resolve) => setTimeout(resolve, 250));
      if (otherActive) { channel.close(); return false; }
      unlockLocalRef.current = () => channel.close();
      return true;
    }
    return new Promise((resolve) => {
      navigator.locks.request(`ai-interview:${token}`, { ifAvailable: true }, (lock) => {
        if (!lock) { resolve(false); return undefined; }
        return new Promise((release) => {
          unlockLocalRef.current = release;
          resolve(true);
        });
      }).catch(() => resolve(false));
    });
  };

  const acquireLease = async () => {
    if (claimedRef.current || claimInFlightRef.current || hiddenRef.current) return;
    const generation = lifecycleRef.current;
    claimInFlightRef.current = true;
    setClaiming(true);
    const local = await acquireLocal();
    if (generation !== lifecycleRef.current || hiddenRef.current) {
      unlockLocalRef.current?.();
      unlockLocalRef.current = null;
      claimInFlightRef.current = false;
      if (!hiddenRef.current) setClaiming(false);
      return;
    }
    if (!local) {
      setLeaseBlocked(true);
      setClaiming(false);
      claimInFlightRef.current = false;
      return;
    }
    try {
      // Each page lifetime has a different owner. A delayed close/reload release
      // must never delete the replacement page's newly acquired lease.
      let owner = ownerRef.current;
      if (!owner) {
        const bytes = new Uint8Array(32);
        crypto.getRandomValues(bytes);
        owner = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
      }
      ownerRef.current = owner;
      let previousOwner = null;
      if (navigator.locks?.request) {
        try { previousOwner = sessionStorage.getItem(ownerStorageKey); } catch { /* storage unavailable */ }
      }
      const claimed = await claimAiLaunch(token, owner,
        previousOwner && previousOwner !== owner ? previousOwner : null);
      if (generation !== lifecycleRef.current || hiddenRef.current) {
        await releaseAiLaunch(token, owner, { keepalive: true }).catch(() => {});
        unlockLocalRef.current?.();
        unlockLocalRef.current = null;
        return;
      }
      claimedRef.current = true;
      try { sessionStorage.setItem(ownerStorageKey, owner); } catch { /* storage unavailable */ }
      setInterviewLease(owner, loseLease, claimed.scheduled_interview_id);
      setLeaseReady(true);
      setLeaseBlocked(false);
    } catch (err) {
      unlockLocalRef.current?.();
      unlockLocalRef.current = null;
      if (err.message === 'Interview in progress in another session') setLeaseBlocked(true);
      else setError(err.message || 'Could not open this interview. Please retry.');
    } finally { claimInFlightRef.current = false; setClaiming(false); }
  };
  acquireLeaseRef.current = acquireLease;

  const finish = useCallback(async () => {
    if (!claimedRef.current) return;
    claimedRef.current = false;
    try { sessionStorage.removeItem(ownerStorageKey); } catch { /* storage unavailable */ }
    setInterviewLease('', null);
    try { await releaseAiLaunch(token, ownerRef.current); } catch { /* lease expires if release fails */ }
    unlockLocalRef.current?.();
    unlockLocalRef.current = null;
    setLeaseReady(false);
    setStatus((previous) => ({ ...previous, attempt_finished: true }));
  }, [token, ownerStorageKey]);

  const loadStatus = async () => {
    try {
      setError('');
      const data = await getAiLaunchStatus(token);
      if (data?.server_time) {
        setServerClockOffset(new Date(data.server_time).getTime() - Date.now());
      }
      if (data?.status === 'ready' && data.session_id && data.candidate_id) {
        const attempt = await getStatus(String(data.session_id), String(data.candidate_id)).catch(() => null);
        if (attempt?.finished) {
          try { sessionStorage.removeItem(ownerStorageKey); } catch { /* storage unavailable */ }
        }
        setStatus({
          ...data,
          attempt_exists: Boolean(attempt?.exists),                 // a run STARTED → rejoin-exempt
          attempt_finished: Boolean(attempt?.exists && attempt?.finished),
        });
      } else {
        setStatus(data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.detail || err.message || 'This interview link is invalid or expired.');
    }
  };

  useEffect(() => {
    loadStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (status?.status !== 'ready' || status.attempt_finished || leaseReady || leaseBlocked || claiming || error) return;
    const end = status.unlock_at && status.duration != null
      ? new Date(status.unlock_at).getTime() + (5 + Number(status.duration)) * 60000 : null;
    if (!status.attempt_exists && end && Date.now() + serverClockOffset > end) return;
    acquireLease();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, status?.attempt_finished, leaseReady, leaseBlocked, claiming, serverClockOffset, error]);

  useEffect(() => {
    if (!leaseBlocked || claiming || status?.attempt_finished || error) return undefined;
    setLeaseRetryAt(Date.now() + 5000);
    const id = setTimeout(() => acquireLeaseRef.current?.(), 5000);
    return () => clearTimeout(id);
  }, [leaseBlocked, claiming, status?.attempt_finished, token, error]);

  useEffect(() => {
    if (!leaseReady) return undefined;
    const heartbeat = async () => {
      if (!claimedRef.current || hiddenRef.current) return;
      const owner = ownerRef.current;
      const generation = lifecycleRef.current;
      try { await renewAiLaunch(token, owner); }
      catch (err) {
        if (claimedRef.current && ownerRef.current === owner && lifecycleRef.current === generation
            && err.message === 'Interview in progress in another session') loseLease();
      }
    };
    const onVisible = () => { if (!document.hidden) heartbeat(); };
    const id = setInterval(heartbeat, 10000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [leaseReady, loseLease, token]);

  useEffect(() => {
    // Fast Refresh replays effects while preserving React state. A released lease
    // must invalidate that preserved readiness before onboarding can continue.
    if (!claimedRef.current) setLeaseReady(false);
    const relinquish = () => {
      if (claimedRef.current) {
        claimedRef.current = false;
        releaseAiLaunch(token, ownerRef.current, { keepalive: true, markAway: true }).catch(() => {});
      }
      setInterviewLease('', null);
      unlockLocalRef.current?.();
      unlockLocalRef.current = null;
    };
    const onHide = () => {
      hiddenRef.current = true;
      lifecycleRef.current += 1;
      relinquish();
      ownerRef.current = '';
      setLeaseReady(false);
      setAccepted(false);
    };
    const onShow = (event) => {
      const wasHidden = hiddenRef.current;
      hiddenRef.current = false;
      if (event.persisted || wasHidden) {
        setLeaseReady(false);
        setAccepted(false);
        setClaiming(claimInFlightRef.current);
        setLeaseBlocked(true);
      }
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    return () => {
      lifecycleRef.current += 1;
      relinquish();
      ownerRef.current = '';
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
    };
  }, [token]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const unlockAt = status?.unlock_at ? new Date(status.unlock_at).getTime() : null;
  const remaining = unlockAt ? unlockAt - (now + serverClockOffset) : 0;

  // Track the largest remaining we've seen so the progress bar fills smoothly toward unlock.
  useEffect(() => {
    if (status?.status === 'locked' && remaining > maxRemaining) setMaxRemaining(remaining);
  }, [status?.status, remaining, maxRemaining]);
  const progress = maxRemaining > 0 ? Math.min(1, Math.max(0, 1 - remaining / maxRemaining)) : 0;

  useEffect(() => {
    if (status?.status !== 'locked' || !unlockAt) return undefined;

    let unlockTimer;
    const scheduleUnlockCheck = () => {
      const delay = unlockAt - (Date.now() + serverClockOffset);
      if (delay <= 0) {
        loadStatus();
        return;
      }
      unlockTimer = setTimeout(scheduleUnlockCheck, Math.min(delay + 250, 2_147_483_647));
    };
    scheduleUnlockCheck();
    return () => clearTimeout(unlockTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status, unlockAt, serverClockOffset, token]);

  const scheduleText = useMemo(
    () => prettySchedule(status?.scheduled_date, status?.scheduled_time, status?.timezone),
    [status],
  );

  // Live interview — hand off to the proctored experience.
  if (status?.status === 'ready' && accepted && leaseReady && status.session_id && status.candidate_id) {
    return (
      <InterviewExperience
        sessionId={String(status.session_id)}
        candidateId={String(status.candidate_id)}
        companyId={String(status.company_id || '0')}
        onFinished={finish}
      />
    );
  }

  // Already completed.
  if (status?.status === 'ready' && status.attempt_finished) {
    return (
      <Shell>
        <BrandMark tone="success" />
        <h1 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">This interview has ended</h1>
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          Thank you for completing your interview. Your responses have been submitted to the hiring team.
        </p>
        <p className="mt-6 text-xs text-slate-400 dark:text-slate-500">You can safely close this window.</p>
      </Shell>
    );
  }

  // Late INITIAL open — the session window (scheduled + duration) has passed and no run ever started.
  // A rejoin (attempt_exists) is exempt and continues normally. unlock_at is absolute (= scheduled − 5min),
  // so the window end is timezone-safe: unlock_at + (5 + duration) minutes.
  if (status?.status === 'ready' && !status.attempt_exists && unlockAt && status.duration != null) {
    const windowEndMs = unlockAt + (5 + Number(status.duration || 0)) * 60000;
    if ((now + serverClockOffset) > windowEndMs) {
      return (
        <Shell>
          <BrandMark tone="muted" />
          <h1 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">This interview has ended</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
            The interview window has closed, so it can no longer be started. The hiring team has been
            notified that you weren’t able to join in time.
          </p>
          <p className="mt-6 text-xs text-slate-400 dark:text-slate-500">You can safely close this window.</p>
        </Shell>
      );
    }
  }

  // ── Loading ──
  if (!status && !error) {
    return (
      <Shell>
        <BrandMark />
        <h1 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">Interview By {appName}</h1>
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent dark:border-violet-500" />
          Checking your interview link…
        </div>
      </Shell>
    );
  }

  // ── Error / invalid link ──
  if (error) {
    return (
      <Shell>
        <BrandMark tone="error" />
        <h1 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">We couldn't open this interview</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{error}</p>
        <p className="mt-6 text-xs text-slate-400 dark:text-slate-500">
          If you believe this is a mistake, please contact the recruiter who sent you this link.
        </p>
      </Shell>
    );
  }

  if (status?.status === 'ready' && leaseBlocked) {
    return <Shell>
      <BrandMark tone="muted" />
      <h1 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">Interview in progress in another session</h1>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Close the other interview window. Availability is checked automatically; this page will continue when the session is free.
      </p>
      <p role="status" className="mt-3 text-sm text-slate-600 dark:text-slate-300">
        {claiming ? 'Checking availability...' : `Checking again in ${Math.min(5, Math.max(0, Math.ceil((leaseRetryAt - now) / 1000)))}s`}
      </p>
      <button type="button" onClick={acquireLease} disabled={claiming || now < leaseRetryAt}
        className="mt-6 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
        {claiming ? 'Checking…' : 'Retry'}
      </button>
    </Shell>;
  }

  // ── Ready: welcome + begin ──
  if (status.status === 'ready') {
    return (
      <Shell>
        <BrandMark />
        <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-500 dark:text-violet-400">Interview By {appName}</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
          {status.candidate_name ? `Welcome, ${String(status.candidate_name).split(' ')[0]}` : 'Welcome to your interview'}
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          {status.title || 'Your AI interview'} is ready. Take a breath, then begin whenever you're comfortable.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-2 text-left">
          {[
            { t: 'Find a quiet, well-lit spot', d: 'Minimise background noise and interruptions.' },
            { t: 'Have your microphone ready', d: 'You’ll speak with the AI interviewer.' },
            { t: 'Speak naturally', d: 'Answer at your own pace — there’s no rush.' },
          ].map((it) => (
            <div key={it.t} className="flex items-start gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5 dark:bg-white/[0.04]">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm leading-tight text-slate-700 dark:text-slate-200">
                <span className="font-semibold">{it.t}</span>
                <span className="block text-xs text-slate-400 dark:text-slate-500">{it.d}</span>
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => { if (leaseReady && claimedRef.current && !hiddenRef.current) setAccepted(true); }}
          disabled={!leaseReady}
          className="mt-7 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:focus:ring-offset-[#15151f]"
        >
          {leaseReady ? 'Begin interview' : 'Checking interview availability…'}
        </button>
        <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">You'll set up your microphone on the next screen.</p>
      </Shell>
    );
  }

  // ── Locked: countdown to unlock ──
  const { h, m, s } = countdownParts(remaining);
  return (
    <Shell>
      <BrandMark />
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-500 dark:text-violet-400">AI Interview By {appName}</p>
      <h1 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{status.title || 'Your interview'}</h1>

      {scheduleText && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-white/[0.06] dark:text-slate-300">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
          </svg>
          {scheduleText}
        </div>
      )}

      <div className="mt-7 rounded-2xl border border-slate-100 bg-slate-50/70 px-5 py-6 dark:border-white/[0.06] dark:bg-white/[0.03]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Unlocks in</p>
        <div className="mt-4 flex items-center justify-center gap-2.5">
          <TimeTile value={h} label="Hours" />
          <Colon />
          <TimeTile value={m} label="Minutes" />
          <Colon />
          <TimeTile value={s} label="Seconds" highlight />
        </div>
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-1000 ease-linear"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      </div>

      <p className="mt-5 text-sm leading-6 text-slate-500 dark:text-slate-400">
        This page unlocks automatically — no need to refresh. The link opens
        {' '}<span className="font-semibold text-slate-700 dark:text-slate-200">5 minutes before</span> the scheduled start time.
      </p>
    </Shell>
  );
};

export default AIInterviewLaunchPage;
